<script>
import BarcodeGenerator from '@/components/admin/common/BarcodeGenerator.vue'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import QRCode from 'qrcode'

const PRINT_AGENT_BASE_URL = 'http://127.0.0.1:18181'
const PRINT_CLAIM_STORAGE_PREFIX = 'aone:pdf-preview:waybill-print-claim:v1:'
const today = new Date()
const year = today.getFullYear()
const month = today.getMonth() + 1
const date = today.getDate()
const time = today.getTime()

export default {
  name: 'Pdf-preview',
  props: {
    parcelList: Array,
    hideBackground: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close-modal'],
  components: {
    BarcodeGenerator
  },
  data() {
    return {
      pdfKey: 0,
      addrResults: {},
      qrGenerationPromises: {},
      printing: false,
      agentPrinting: false,
      browserPrinting: false,
      timestamp: ''
    }
  },
  watch: {
    parcelList: {
      immediate: true,
      deep: true,
      handler(newList) {
        if (!Array.isArray(newList)) {
          return
        }
        this.$nextTick(() => {
          newList.forEach((item, index) => {
            this.$log.debug('[ watch / parcelList / item ]', item.hblNo)
            this.fetchAddr(item.receiverZipcode).catch((err) => {
              this.$log.error('[ watch / parcelList / fetchAddr ]', err)
            })
            this.getOrCreateQRCodePromise(item.hblNo, index).catch((err) => {
              this.$log.error('[ watch / parcelList / generateQRCode ]', err)
            })
          })
        })
      }
    }
  },
  methods: {
    createWaybillPrintClaimToken() {
      return `pdf-${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`
    },
    getWaybillPrintClaimStorageKey(orderId) {
      return `${PRINT_CLAIM_STORAGE_PREFIX}${Number(orderId)}`
    },
    readStoredWaybillPrintClaim(orderId) {
      if (typeof localStorage === 'undefined') return null
      try {
        const record = JSON.parse(localStorage.getItem(this.getWaybillPrintClaimStorageKey(orderId)) || 'null')
        return record?.orderId && record?.claimToken ? record : null
      } catch (_) {
        return null
      }
    },
    persistWaybillPrintClaim(record) {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(this.getWaybillPrintClaimStorageKey(record.orderId), JSON.stringify(record))
      }
    },
    clearStoredWaybillPrintClaim(orderId, claimToken) {
      if (typeof localStorage === 'undefined') return
      const stored = this.readStoredWaybillPrintClaim(orderId)
      if (stored?.claimToken === claimToken) {
        localStorage.removeItem(this.getWaybillPrintClaimStorageKey(orderId))
      }
    },
    getOrCreateWaybillPrintClaim(item) {
      const orderId = Number(item?.id)
      const partnerId = Number(item?.partnerId)
      if (!orderId || !partnerId) {
        throw new Error('운송장 출력 claim에 필요한 주문 또는 파트너 정보가 없습니다.')
      }
      const stored = this.readStoredWaybillPrintClaim(orderId)
      if (stored && Number(stored.partnerId) === partnerId) return stored
      const record = {
        orderId,
        partnerId,
        claimToken: this.createWaybillPrintClaimToken(),
        createdAt: new Date().toISOString()
      }
      this.persistWaybillPrintClaim(record)
      return record
    },
    async requestWaybillPrintClaim(record) {
      const res = await this.$http({
        method: 'POST',
        url: '/api/fulfillment/packing/waybill-print-claim',
        data: { id: record.orderId, partnerId: record.partnerId, claimToken: record.claimToken },
        name: '[ Axios / PDF preview / 운송장 출력 claim ]'
      })
      const response = res?.data || {}
      if (response.ok !== true) throw new Error(response.message || '운송장 출력 claim에 실패했습니다.')
      const claim = response.data || {}
      if (claim.alreadyPrinted) throw new Error('이미 출력 처리된 주문이 포함되어 있습니다.')
      if (claim.claimToken !== record.claimToken) throw new Error('서버 claim 토큰이 요청 토큰과 일치하지 않습니다.')
    },
    async releaseWaybillPrintClaim(record) {
      const res = await this.$http({
        method: 'POST',
        url: '/api/fulfillment/packing/waybill-print-release',
        data: { id: record.orderId, partnerId: record.partnerId, claimToken: record.claimToken },
        name: '[ Axios / PDF preview / 운송장 출력 claim 해제 ]'
      })
      if (res?.data?.ok !== true) throw new Error(res?.data?.message || '운송장 출력 claim 해제에 실패했습니다.')
      this.clearStoredWaybillPrintClaim(record.orderId, record.claimToken)
    },
    async finalizeWaybillPrintClaim(record) {
      const res = await this.$http({
        method: 'POST',
        url: '/api/fulfillment/packing/waybill-print',
        data: { id: record.orderId, partnerId: record.partnerId, claimToken: record.claimToken },
        name: '[ Axios / PDF preview / 운송장 출력 완료 ]'
      })
      if (res?.data?.ok !== true) throw new Error(res?.data?.message || '운송장 출력 완료 처리에 실패했습니다.')
      this.clearStoredWaybillPrintClaim(record.orderId, record.claimToken)
    },
    closeModal() {
      this.$emit('close-modal')
    },
    getFileName() {
      const fileName = `${year}${month >= 10 ? month : '0' + month}${date >= 10 ? date : '0' + date}${time}`
      return navigator.userAgent.indexOf('MSIE') !== -1 || navigator.appVersion.indexOf('Trident/') > -1 ? `${fileName}.pdf` : fileName
    },
    async exportAllWaybillsToPDF() {
      if (this.printing) return
      this.printing = true
      this.timestamp = new Date().toLocaleString()

      try {
        await new Promise((resolve) => setTimeout(resolve, 100))
        const pdf = await this.createWaybillPdf()
        pdf.save(this.getFileName() + '.pdf')

        const payload = {
          id: this.parcelList.map((item) => item.id)
        }

        const config = {
          method: 'POST',
          url: '/api/parcel/setOrderPrinted',
          data: payload,
          name: '[ Axios / setOrderPrinted ]'
        }

        const res = await this.$http(config)
        this.$log.debug('출력완료', res.data)
        this.parcelList.forEach((item) => {
          item.isPrinted = 1
        })
      } catch (err) {
        this.$log.error('[ exportAllWaybillsToPDF ]', err)
        this.$q?.notify?.({
          type: 'negative',
          message: err?.message || 'PDF 생성에 실패했습니다.',
          position: 'top',
          timeout: 3000
        })
      } finally {
        this.printing = false
        this.timestamp = ''
      }
    },
    async prepareForCapture() {
      if (!Array.isArray(this.parcelList) || this.parcelList.length === 0) {
        return
      }

      await Promise.all(this.parcelList.map((item) => this.fetchAddr(item.receiverZipcode)))
      await this.$nextTick()
      await Promise.all(this.parcelList.map((item, index) => this.getOrCreateQRCodePromise(item.hblNo, index)))
      await this.$nextTick()
    },
    async createWaybillPdf() {
      await this.prepareForCapture()

      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'cm',
        format: [17, 11]
      })

      const waybills = this.$refs.pdfArea.querySelectorAll('.waybill')
      for (let i = 0; i < waybills.length; i++) {
        const wb = waybills[i]
        const canvas = await html2canvas(wb, {
          scale: 2,
          useCORS: true,
          backgroundColor: null
        })
        const imgData = canvas.toDataURL('image/jpeg', 1.0)
        pdf.addImage(imgData, 'JPEG', 0, 0, 17, 11)

        if (i < waybills.length - 1) {
          pdf.addPage([17, 11], 'landscape')
        }
      }

      return pdf
    },
    buildBrowserPrintHtml() {
      const pdfArea = this.$refs.pdfArea
      if (!pdfArea) {
        throw new Error('운송장 영역을 찾을 수 없습니다.')
      }

      return `<!doctype html>
<html lang="ko">
<head>
  <meta charset="utf-8" />
  <title>AONE Waybill Browser Print</title>
  <style>
    @page { size: 17cm 11cm; margin: 0; }
    * { box-sizing: border-box; }
    html, body {
      width: 17cm;
      margin: 0;
      padding: 0;
      background: #fff;
      color: #000;
      font-family: Arial, sans-serif;
    }
    .pdfArea {
      width: 17cm;
      margin: 0 auto;
      padding: 0;
      background: #fff;
    }
    .waybill {
      width: 17cm !important;
      height: 11cm !important;
      margin: 0 !important;
      padding: 0 !important;
      border: none !important;
      box-shadow: none !important;
      page-break-after: always;
      break-after: page;
      background-color: #fff;
      background-image: none !important;
    }
    .waybill:last-child {
      page-break-after: auto;
      break-after: auto;
    }
    .waybill-list {
      margin: 0;
      padding: 0;
      font-size: .825rem;
    }
    .waybill-list li {
      list-style: none;
    }
    .row {
      display: flex;
      flex-wrap: wrap;
    }
    .col {
      flex: 10000 1 0%;
    }
    .col-auto {
      flex: 0 0 auto;
    }
    .col-3 {
      width: 25%;
    }
    .col-4 {
      width: 33.3333%;
    }
    .col-5 {
      width: 41.6667%;
    }
    .text-center {
      text-align: center;
    }
    .text-right {
      text-align: right;
    }
    .wb-right-col {
      position: relative;
      top: 0;
      right: 0;
      width: 58px;
      height: 363px;
      box-sizing: border-box;
      overflow: hidden;
    }
    .wb-rotor {
      position: absolute;
      top: -58px;
      right: -10px;
      transform-origin: bottom right;
      transform: rotate(-90deg);
      width: 363px;
      height: 58px;
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .wb-rotor > * {
      max-width: 40%;
    }
    .wb-line {
      display: flex;
      flex-direction: row;
      align-items: center;
      font-size: 10px;
      white-space: nowrap;
    }
    .wb-value {
      font-weight: 500;
    }
    img, svg, canvas {
      max-width: 100%;
    }
  </style>
</head>
<body>
  <div class="pdfArea">${pdfArea.innerHTML}</div>
</body>
</html>`
    },
    async printWaybillsInBrowser() {
      if (this.browserPrinting) return

      this.browserPrinting = true
      this.timestamp = new Date().toLocaleString()

      await this.$nextTick()

      const iframe = document.createElement('iframe')
      iframe.setAttribute('title', 'AONE Waybill Browser Print')
      iframe.style.position = 'fixed'
      iframe.style.right = '0'
      iframe.style.bottom = '0'
      iframe.style.width = '0'
      iframe.style.height = '0'
      iframe.style.border = '0'
      iframe.style.opacity = '0'

      document.body.appendChild(iframe)

      try {
        const printWindow = iframe.contentWindow
        const printDocument = iframe.contentDocument || printWindow?.document
        if (!printWindow || !printDocument) {
          throw new Error('출력 프레임을 생성할 수 없습니다.')
        }

        printDocument.open()
        printDocument.write(this.buildBrowserPrintHtml())
        printDocument.close()

        await new Promise((resolve) => setTimeout(resolve, 300))
        printWindow.focus()
        printWindow.print()

        this.$q.notify({
          type: 'positive',
          message: '브라우저 즉시 출력 요청을 전송했습니다.',
          position: 'top',
          timeout: 2000
        })
      } catch (err) {
        this.$q.notify({
          type: 'negative',
          message: err?.message || '브라우저 즉시 출력에 실패했습니다.',
          position: 'top',
          timeout: 3000
        })
      } finally {
        setTimeout(() => {
          iframe.remove()
          this.browserPrinting = false
        }, 1000)
      }
    },
    async printWaybillsViaAgent() {
      if (this.agentPrinting) return

      this.agentPrinting = true
      this.timestamp = new Date().toLocaleString()
      const acquiredClaims = []
      let dispatchStarted = false

      try {
        for (const item of this.parcelList || []) {
          const record = this.getOrCreateWaybillPrintClaim(item)
          await this.requestWaybillPrintClaim(record)
          acquiredClaims.push(record)
        }
        if (acquiredClaims.length === 0) throw new Error('출력할 운송장이 없습니다.')

        await this.$nextTick()
        const pdf = await this.createWaybillPdf()
        dispatchStarted = true
        const response = await fetch(`${PRINT_AGENT_BASE_URL}/print/pdf`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            pdfBase64: pdf.output('datauristring')
          })
        })
        const data = await response.json().catch(() => ({}))
        if (!response.ok || data?.ok !== true) {
          throw new Error(data?.message || '로컬 프린트 에이전트 출력 요청에 실패했습니다.')
        }

        for (const record of acquiredClaims) {
          await this.finalizeWaybillPrintClaim(record)
        }

        this.$q.notify({
          type: 'positive',
          message: '로컬 에이전트로 출력 요청을 전송했습니다.',
          position: 'top',
          timeout: 2000
        })
      } catch (err) {
        if (!dispatchStarted) {
          for (const record of acquiredClaims.reverse()) {
            await this.releaseWaybillPrintClaim(record).catch((releaseErr) => {
              this.$log?.error?.('[ PDF preview / 출력 전 claim 해제 실패 ]', releaseErr)
            })
          }
        }
        this.$q.notify({
          type: 'negative',
          message: err?.message || '로컬 에이전트 출력에 실패했습니다.',
          position: 'top',
          timeout: 3000
        })
      } finally {
        this.agentPrinting = false
      }
    },
    async addrApi(zipcode) {
      this.$log.debug(`[addrApi] 주소 API 호출 시작. Zipcode: ${zipcode}`)
      const config = {
        method: 'GET',
        url: `/api/parcel/lookup/${zipcode}`,
        name: '[ Axios / 주소조회-프록시 ]'
      }

      return await this.$http(config)
        .then((res) => {
          this.$log.debug(`[addrApi] 주소 조회 성공. Zipcode: ${zipcode}, 응답 데이터:`, res.data)
          this.$log.debug('주소 조회 성공', res.data)
          return res.data
        })
        .catch((err) => {
          console.error(`[addrApi] 주소 조회 실패. Zipcode: ${zipcode}, 오류:`, err)
          this.$log.error('주소 조회 실패', err)
          throw err
        })
    },
    async fetchAddr(zipcode) {
      this.$log.debug(`[fetchAddr] 주소 가져오기 시작. Zipcode: ${zipcode}`)
      if (!zipcode) {
        console.warn('[fetchAddr] Zipcode가 없습니다.')
        return ''
      }
      if (this.addrResults[zipcode]) {
        this.$log.debug(`[fetchAddr] 캐시된 주소 사용. Zipcode: ${zipcode}`)
        return this.addrResults[zipcode]
      }
      const xmlStr = await this.addrApi(zipcode)
      const parser = new DOMParser()
      const xmlDoc = parser.parseFromString(xmlStr, 'text/xml')
      const buildedHtml = this.bagCodeBuilder(xmlDoc)
      this.addrResults[zipcode] = buildedHtml
      return buildedHtml
    },
    getAddrResult(zipcode) {
      return this.addrResults[zipcode] || {}
    },
    bagCodeBuilder(xmlDoc) {
      const detailCodes = xmlDoc.getElementsByTagName('cod3')[0]?.textContent || ''
      const bunryuCode = detailCodes.slice(0, 2)
      const bagName = xmlDoc.getElementsByTagName('cod1')[0]?.textContent || ''
      const destinationName = xmlDoc.getElementsByTagName('cod2')[0]?.textContent || ''
      const detailCode1 = detailCodes.slice(2, 4)
      const detailCode2 = detailCodes.slice(4, 6)
      const detailCode3 = detailCodes.slice(6, 9)
      const destinationCode = xmlDoc.getElementsByTagName('cod0')[0]?.textContent || ''

      return {
        bunryuCode,
        bagName,
        destinationCode,
        destinationName,
        detailCode1,
        detailCode2,
        detailCode3
      }
    },
    async getOrCreateQRCodePromise(hblNo, index) {
      const key = `${index}:${String(hblNo ?? '').trim()}`
      if (this.qrGenerationPromises[key]) {
        return this.qrGenerationPromises[key]
      }

      const generationPromise = this.generateQRCode(hblNo, index)
      this.qrGenerationPromises[key] = generationPromise
      try {
        await generationPromise
      } finally {
        if (this.qrGenerationPromises[key] === generationPromise) {
          delete this.qrGenerationPromises[key]
        }
      }
    },
    async generateQRCode(hblNo, index) {
      this.$log.debug('[ generateQRCode ]', hblNo)
      const containers = this.$refs.qrContainer
      const container = Array.isArray(containers) ? containers[index] : containers
      if (!container) {
        throw new Error(`QR 컨테이너를 찾을 수 없습니다. index=${index}`)
      }
      const data = String(hblNo ?? '').trim()
      if (!data) {
        this.$log.debug('[ generateQRCode ] invalid data', hblNo)
        throw new Error('QR 생성에 필요한 운송장 번호가 없습니다.')
      }
      container.innerHTML = ''
      return new Promise((resolve, reject) => {
        QRCode.toCanvas(data, { width: 58 }, (err, canvas) => {
          this.$log.debug('[ generateQRCode ] !err && canvas = ', !err && canvas, err, canvas)
          if (err || !canvas) {
            reject(err || new Error('QR canvas 생성에 실패했습니다.'))
            return
          }
          this.$log.debug('[ generateQRCode ]', data, canvas)
          container.appendChild(canvas)
          resolve()
        })
      })
    },
    async generateAllQRCodes() {
      if (!Array.isArray(this.parcelList)) {
        return
      }
      this.$nextTick(() => {
        this.parcelList.forEach((item, index) => {
          this.getOrCreateQRCodePromise(item.hblNo, index).catch((err) => {
            this.$log.error('[ generateAllQRCodes / generateQRCode ]', err)
          })
        })
      })
    }
  },
  mounted() {
    this.$log.debug('[ mounted ] parcelList = ', this.parcelList)
  }
}
</script>

<template>
  <q-layout
    container
    view="lHh lpr lFf"
  >
    <q-header
      class="bg-primary"
      elevated
    >
      <q-toolbar>
        <q-toolbar-title>운송장 출력 미리보기</q-toolbar-title>
        <q-btn
          flat
          v-close-popup
          round
          dense
          icon="close"
          @click="closeModal"
        />
      </q-toolbar>
    </q-header>

    <q-footer
      class="bg-white text-black"
      elevated
    >
      <div class="row q-col-gutter-sm q-pa-sm">
        <div class="col-12 col-sm-6">
          <q-btn
            label="PDF 다운로드"
            size="lg"
            class="full-width"
            color="primary"
            :loading="printing"
            @click="exportAllWaybillsToPDF"
          />
        </div>
        <div class="col-12 col-sm-6">
          <q-btn
            label="로컬 에이전트 즉시 출력"
            size="lg"
            class="full-width"
            color="secondary"
            :loading="agentPrinting"
            @click="printWaybillsViaAgent"
          />
        </div>
      </div>
    </q-footer>

    <q-page-container>
      <q-page padding>
        <div class="row">
          <div class="col">
            <div
              class="pdfArea"
              ref="pdfArea"
              :key="pdfKey"
            >
              <q-card
                v-for="(item, index) in parcelList"
                :key="index"
                :class="['waybill', { 'none-background': hideBackground || printing || agentPrinting }]"
                cellpadding="0"
                cellmargin="0"
              >
                <q-card-section>
                  <div class="row" style="margin-top:0cm;">
                    <div class="col col-4" style="margin-top: 0.9cm; width:5.8cm;">
                      <ul class="waybill-list">
                        <li style="position:relative;">
                          접수국 : 국제우편물류센터
                          <div style="position:absolute; bottom:0; right:0; width:58px; height:58px;">
                            <div ref="qrContainer" />
                          </div>
                        </li>
                      </ul>
                      <ul class="waybill-list" style="font-size: 12px; font-weight: 700; height: 1.8cm; margin-top: 0.1cm; line-height: 1.2;">
                        <li>주문인 : {{ item.shipperName }}</li>
                        <li>고객 주문처 : {{ item.shippingAgencyName }}</li>
                        <li>문의처 : 02-2665-4008</li>
                        <li>주문번호 : {{ item.orderId }}</li>
                        <li>BOX : {{ item.boxQty }} 중량 : {{ item.weight }} CM 요금 : 계약</li>
                      </ul>
                      <ul class="waybill-list" style="font-size: 9px; margin-top: 0.3cm; height: 2.2cm;">
                        <li class="text-center">
                          <BarcodeGenerator
                            :value="item.receiverZipcode"
                            :lineColor="'#000'"
                            :width="1.4"
                            :height="40"
                            :elementTag="'img'"
                          />
                        </li>
                      </ul>
                      <ul class="waybill-list" style="font-size: 16px; height: 4cm;">
                        <li v-show="item.orderMemo !== '' && item.orderMemo !== null">
                          배송메시지 : {{ item.orderMemo }}
                        </li>
                        <li v-show="item.itemNames !== '' && item.itemNames !== null">
                          아이템 목록: {{ item.itemNames }}
                        </li>
                      </ul>
                    </div>

                    <div class="col col-5" style="width: 8.8cm;">
                      <ul class="waybill-list" style="padding-left: 0.2cm;padding-right: 0.2cm; height: 1.4cm;">
                        <li>
                          <div class="row" style="font-size: 16px; font-weight: 700; align-items: center; line-height: 1; letter-spacing:-1px;">
                            <div class="col" style="font-size: 42px; font-weight: 900; text-align: center;">
                              {{ getAddrResult(item.receiverZipcode).bunryuCode }}
                            </div>
                            <div class="col col-auto" style="text-align: center;">
                              {{ getAddrResult(item.receiverZipcode).bagName }}
                            </div>
                            <div class="col" style="font-size: 42px; font-weight: 900; text-align: center; letter-spacing:-2px;">
                              {{ getAddrResult(item.receiverZipcode).destinationCode }}
                            </div>
                            <div class="col col-auto" style="text-align: center;">
                              {{ getAddrResult(item.receiverZipcode).destinationName }}
                            </div>
                            <div class="col col-3" style="font-size: 26px; font-weight: 700; width:25%; text-align: center;">
                              {{ getAddrResult(item.receiverZipcode).detailCode1 }} {{ getAddrResult(item.receiverZipcode).detailCode2 }}<br>{{ getAddrResult(item.receiverZipcode).detailCode3 }}
                            </div>
                          </div>
                        </li>
                      </ul>
                      <ul class="waybill-list" style="font-size: 14px; font-weight: 700; padding-left: 0.55cm; padding-right: 0.7cm; padding-top: 0.4cm; height: 1.8cm;">
                        <li>
                          <div class="row">
                            <div class="col">
                              {{ item.partnerName || item.shipperName }}
                            </div>
                            <div class="col text-right">
                              {{ this.$formatPhoneNumber(item.shipperMobile, 'mobile') }}
                            </div>
                          </div>
                        </li>
                        <li>{{ item.shipperAddress }}</li>
                      </ul>
                      <ul class="waybill-list" style="padding-left: 0.55cm; padding-right: 0.6cm; padding-top:0.2cm; height: 4cm;">
                        <li>{{ item.receiverAddress1 + ' ' + item.receiverAddress2 }}</li>
                        <li>{{ item.receiverName }}</li>
                        <li>{{ this.$formatPhoneNumber(item.receiverMobile, 'mobile') }}</li>
                      </ul>
                      <ul class="waybill-list" style="padding-top:0.1cm">
                        <li class="text-center">
                          <BarcodeGenerator
                            :value="item.hblNo"
                            :lineColor="'#000'"
                            :width="1.6"
                            :height="45"
                            :elementTag="'img'"
                          />
                        </li>
                      </ul>
                    </div>

                    <div class="col wb-right-col">
                      <div class="wb-rotor">
                        <BarcodeGenerator
                          :value="item.hblNo"
                          format="CODE128"
                          :width="2"
                          :height="40"
                          line-color="#000"
                          background="#fff"
                        />
                        <div class="wb-line">
                          <div class="wb-value">{{ item.orderId || '오더번호 없음' }}</div>
                        </div>
                        <div class="wb-line">
                          <div class="wb-value">{{ item.receiverName }}</div>
                        </div>
                        <div class="wb-line">
                          <div class="wb-value">{{ timestamp }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </q-card-section>
              </q-card>
            </div>
          </div>
        </div>
      </q-page>
    </q-page-container>
  </q-layout>
</template>

<style scoped lang="scss">
.pdfArea {
  width: 17cm;
  margin: 0 auto;
  padding: 0;
  background: white;

  .waybill {
    width: 17cm !important;
    height: 11cm !important;
    margin: 0 !important;
    padding: 0 !important;
    border: none !important;
    box-shadow: none !important;
    page-break-after: always;
    break-after: page;
    background-color: white;
    background-image: url("@/assets/imgs/background/type-c.jpeg");
    background-size: cover;
    background-position: 0 0;
  }

  .waybill-list {
    margin: 0;
    padding: 0;
    font-size: .825rem;

    li {
      list-style: none;
    }
  }
}

@media print {
  .pdfArea {
    margin: 0 auto;
    padding: 0;

    .waybill {
      background: none;
      page-break-after: always;
      break-after: page;
    }
  }
}

@media screen {
  .pdfArea {
    margin: 0 auto;

    .waybill {
      page-break-after: always;
      break-after: page;

      &.none-background {
        background-image: none;
        background-size: none;
        background-position: none;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}

.q-dialog__inner--minimized > div {
  max-width: 20cm;
}

.wb-right-col {
  position: relative;
  top: 0;
  right: 0;
  width: 58px;
  height: 363px;
  box-sizing: border-box;
  overflow: hidden;
}

.wb-rotor {
  position: absolute;
  top: -58px;
  right: -10px;
  transform-origin: bottom right;
  transform: rotate(-90deg);
  width: 363px;
  height: 58px;
  display: flex;
  align-items: center;
  gap: 10px;

  & > * {
    max-width: 40%;
  }
}

.wb-line {
  display: flex;
  flex-direction: row;
  align-items: center;
  font-size: 10px;
  white-space: nowrap;
}

.wb-label {
  font-weight: 700;
  margin-right: 4px;
}

.wb-value {
  font-weight: 500;
}

.wb-rotor barcodeGenerator,
.wb-rotor svg,
.wb-rotor canvas {
  display: block;
}
</style>
